import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import { List } from 'immutable';

import t from '../../../lib/I18n';

import { defer, isEmpty, noop } from 'lodash';

import BaseModal from '../../general/BaseModal';
import Radio from '../../form/Radio';
import Icon from '../../general/Icon';
import BAlert, { bAlertType } from '../../general/BAlert';
import { GlobalModals, JourneyDirections, ReservationErrorCodes, ReservationBundleOutcome, SeatingPrefs } from '../../../constants/AppConstants';
import SeatingPreferences from '../../../records/SeatingPreferences';
import RadioGroups from '../reservations/RadioGroups';
import FeatureCheckboxes from '../reservations/FeatureCheckboxes';
import { useStoreState } from '../../../stores/useStoreState';
import CurrentTripStore from '../../../stores/CurrentTripStore';
import { stationDisplayName } from '../../../utils/reservationUtils';
import ReservationRequestActions from '../../../actions/ReservationRequestActions';
import ReservationActions from '../../../actions/ReservationActions';
import ModalActions from '../../../actions/ModalActions';
import TripActions from '../../../actions/TripActions';
import { ReservationContexts } from '../../../constants/AppConstants';
import classNames from 'classnames';
import { radioGroups } from './RadioGroupsData';


export const extractErrorMessage = (err) =>
    typeof err === 'string'
        ? err
        : (err && err.message) || 'An unexpected error occurred when trying to create a reservation';

const AddReservationModal = (props) => {
    const {
        show = true,
        onClose,
        onContinue,
        context = ReservationContexts.TICKET_BOOKING, // default to ticket booking context
        currentJourneyData, // used for booking via the ListView
    } = props;
    const trip = useStoreState(CurrentTripStore);
    const isSeatingAvailable = trip.isSeatingAvailable()
        || (currentJourneyData
            && currentJourneyData.service
            && currentJourneyData.service.isSeatAvailable(currentJourneyData.fare));

    const [seatSelected, setSeatSelected] = useState(false);
    const [bikeSelected, setBikeSelected] = useState(false);
    const [bikesQty, setBikesQty] = useState(1);
    const [seatingPreferences, setSeatingPreferences] = useState(new SeatingPreferences({
        outward: !!trip.outwardJourney,
        inward: !!trip.returnjourney,
    }));
    const [showSeatPrefError, setShowSeatPrefError] = useState(false);
    const [tripHasBikeReservationCapability, setTripHasBikeReservationCapability] = useState(true);
    const [tripHasSeatingCapability, setTripHasSeatingCapability] = useState(true);
    const [bikeAvailabilityError, setBikeAvailabilityError] = useState(false);

    const { search } = trip;
    const { origin, destination, adults, children } = search;

    const adultCount = adults || 0;
    const childrenCount = children || 0;
    const totalPassengerCount = adultCount + childrenCount;
    const maxBikeQty = Math.max(1, totalPassengerCount); // Don't allow more bikes than passengers

    /*
        Update the amount of bikes in the reservation request store when the bike selection changes.

        This is used to ensure that the reservation request has the correct number of bikes
        when the user clicks continue in the modal.
    */
    useEffect(() => {
        defer(() => {
            ReservationRequestActions.setBikes(bikeSelected ? bikesQty : 0);
        });
    }, [bikeSelected, bikesQty]);

    /*
        When the modal is shown, we need to set the passengers in the reservation request store
        with the current seating preferences and the number of adults and children.
        This is used to ensure that the reservation request has the correct passengers
        when the user clicks continue in the modal.
    */
    useEffect(() => {
        if (show) {
            const attributes = seatingPreferences.toReservationCodes();

            const passengerData = new List([
                ...Array.from({ length: adultCount }).map(() => ({
                    statusCode: '000',
                    attributes: seatSelected ? attributes : [],
                })),
                ...Array.from({ length: childrenCount }).map(() => ({
                    statusCode: '001',
                    attributes: seatSelected ? attributes : [],
                })),
            ]);

            ReservationRequestActions.setPassengers(passengerData);
        }
    }, [show, adultCount, childrenCount, seatingPreferences, seatSelected]);

    /*
        If the user has selected a bike, we need to ensure that the number of bikes
        does not exceed the total number of passengers. This is to prevent the user
        from selecting more bikes than there are passengers, which would not make sense.
    */
    useEffect(() => {
        if (bikeSelected && bikesQty > totalPassengerCount) {
            setBikesQty(totalPassengerCount);
        }
    }, [totalPassengerCount, bikeSelected, bikesQty]);

    /*
        When the modal is shown, we need to check if the trip has seating and bike reservation capabilities.
        This is done by checking the trip's outward and return journeys for their respective services and fares.

        If there is no availability for seating or bike reservations, we disable the respective options in the modal.
    */
    useEffect(() => {
        if (show) {
            // eslint-disable-next-line
            const checkTripAvailability = async () => {
                const dummyPassengerData = new List([
                    ...Array.from({ length: adultCount }).map(() => ({
                        statusCode: '000',
                        attributes: [],
                    })),
                    ...Array.from({ length: childrenCount }).map(() => ({
                        statusCode: '001',
                        attributes: [],
                    })),
                ]);

                /*
                    We get the journeys from the trip for the GridView, or the currentJourneyData for the ListView.

                    For the ListView, only the outward or inward will be present at one time depending on whether
                    the user is choosing the outward or return journey.
                */
                const outwardService = trip.getIn(['outwardJourney', 'journey']) || (currentJourneyData && currentJourneyData.direction === JourneyDirections.OUTWARD ? currentJourneyData.service.journey : null);
                const inwardService = trip.getIn(['returnJourney', 'journey']) || (currentJourneyData && currentJourneyData.direction === JourneyDirections.INWARD ? currentJourneyData.service.journey : null);

                const outwardFare = trip.getOutwardFare() || (currentJourneyData && currentJourneyData.direction === JourneyDirections.OUTWARD ? currentJourneyData.fare : null);
                const inwardFare = trip.getReturnFare() || (currentJourneyData && currentJourneyData.direction === JourneyDirections.INWARD ? currentJourneyData.fare : null);

                const params = {
                    bikesCount: 1,
                    passengers: dummyPassengerData,
                    requestSeats: true,
                    outwardService,
                    inwardService,
                    outwardFare,
                    inwardFare,
                    context,
                    isBasicAvailabilityCheck: true,
                };

                try {
                    const {
                        bundleAvailabilityMap,
                        requiredBundles
                    } = await ReservationActions.checkAvailabilityForReservation(params); // opts, isBookingFlow

                    /*
                        Always check outbound availability for seats and bikes.
                        Additionally check inbound only if it's required.
                    */
                    const outboundSeatAvailable =
                        requiredBundles.includes('SEATS_OUTBOUND') &&
                        bundleAvailabilityMap.SEATS_OUTBOUND === ReservationBundleOutcome.FULL_AVAILABILITY;

                    const inboundSeatAvailable =
                        requiredBundles.includes('SEATS_INBOUND') &&
                        bundleAvailabilityMap.SEATS_INBOUND === ReservationBundleOutcome.FULL_AVAILABILITY;

                    const outboundBikeAvailable =
                        requiredBundles.includes('BIKES_OUTBOUND') &&
                        bundleAvailabilityMap.BIKES_OUTBOUND === ReservationBundleOutcome.FULL_AVAILABILITY;

                    const inboundBikeAvailable =
                        requiredBundles.includes('BIKES_INBOUND') &&
                        bundleAvailabilityMap.BIKES_INBOUND === ReservationBundleOutcome.FULL_AVAILABILITY;

                    /*
                        Capability is enabled only if outbound is available
                        AND (inbound is either not required OR also available).
                    */
                    const hasSeatingAvailability = isSeatingAvailable && outboundSeatAvailable &&
                        (!requiredBundles.includes('SEATS_INBOUND') || inboundSeatAvailable);

                    const hasBikeAvailability = outboundBikeAvailable &&
                        (!requiredBundles.includes('BIKES_INBOUND') || inboundBikeAvailable);

                    // if the check for availability fails, just assume full availability and let them try to book
                    if (isEmpty(bundleAvailabilityMap)) {
                        return false;
                    }

                    setTripHasSeatingCapability(hasSeatingAvailability);
                    setTripHasBikeReservationCapability(hasBikeAvailability);
                } catch (err) {
                    console.error('Error checking availability on show:', err);
                }
            };

            checkTripAvailability();
        }
    }, [trip, adultCount, childrenCount, show, currentJourneyData, context, isSeatingAvailable]);

    /*
        If the trip does not have seating or bike reservation capabilities, we reset the selection states.
        This ensures that if the availability changes after the modal is opened,
        the user cannot select options that are not available.
    */
    useEffect(() => {
        if (!tripHasSeatingCapability) {
            setSeatSelected(false);
        }
        if (!tripHasBikeReservationCapability) {
            setBikeSelected(false);
        }
    }, [tripHasSeatingCapability, tripHasBikeReservationCapability]);

    /*
        Handle the continue button click.
        This function checks if the user has selected a seat or bike, validates the seating preferences,
        and then proceeds to create the reservation or continue with the booking flow.
    */
    const handleContinue = async () => {
        setShowSeatPrefError(false);
        setBikeAvailabilityError(false);

        if (context !== ReservationContexts.TICKET_BOOKING && !seatSelected && !bikeSelected) {
            console.error('No seat or bike selected');
            return;
        }

        /*
            The BE will silently reject any seat reservation request if
            the user has not selected at least one valid seating preference.
        */
        if (seatSelected) {
            const prefs = seatingPreferences.toJS();
            const preferenceFields = ['direction', 'position', 'seatType'];
            const hasAtLeastOneValidPref = preferenceFields.some(
                (field) => prefs[field] !== SeatingPrefs.NONE
            );

            if (!hasAtLeastOneValidPref) {
                setShowSeatPrefError(true);
                return;
            }
        }

        const attributes = seatingPreferences.toReservationCodes();

        const passengerData = new List([
            ...Array.from({ length: adultCount }).map(() => ({
                statusCode: '000',
                attributes: seatSelected ? attributes : [],
            })),
            ...Array.from({ length: childrenCount }).map(() => ({
                statusCode: '001',
                attributes: seatSelected ? attributes : [],
            })),
        ]);

        if (context === ReservationContexts.STANDALONE_BOOKING) {
            // For when the context is the reservation widget via the My Account section
            ReservationRequestActions.setPassengers(passengerData);
            ReservationRequestActions.setRequestSeats(seatSelected);
            ReservationRequestActions.setBikes(bikeSelected ? bikesQty : 0);

            try {
                const { err, reservation } = await ReservationActions.createReservation();
                if (reservation) {
                    onContinue();
                } else {
                    ReservationActions.clearReservationStore();
                    ReservationRequestActions.clearReservationRequestStore();
                    const errorMessage = extractErrorMessage(err);

                     if (err.code === ReservationErrorCodes.RESERVATION_LIMIT_REACHED) {
                        ModalActions.open(GlobalModals.ERROR, { title: "We’re unable to complete your reservation", message: errorMessage });
                    } else {
                        ModalActions.open(GlobalModals.ERROR, { message: errorMessage });
                    }

                    onClose();
                }
            } catch (err) {
                const errorMessage = extractErrorMessage(err);
                ModalActions.open(GlobalModals.ERROR, { message: errorMessage });
            }
        } else { // context is the booking flow
            // For when the context is the booking flow, either Grid or List View.
            if (seatSelected || bikeSelected) {
                const outwardService = trip.getIn(['outwardJourney', 'journey']) || (currentJourneyData && currentJourneyData.direction === JourneyDirections.OUTWARD ? currentJourneyData.service.journey : null);
                const inwardService = trip.getIn(['returnJourney', 'journey']) || (currentJourneyData && currentJourneyData.direction === JourneyDirections.INWARD ? currentJourneyData.service.journey : null);

                const outwardFare = trip.getOutwardFare() || (currentJourneyData && currentJourneyData.direction === JourneyDirections.OUTWARD ? currentJourneyData.fare : null);
                const inwardFare = trip.getReturnFare() || (currentJourneyData && currentJourneyData.direction === JourneyDirections.INWARD ? currentJourneyData.fare : null);

                const params = {
                    bikesCount: bikeSelected ? bikesQty : 0,
                    passengers: passengerData,
                    requestSeats: seatSelected,
                    outwardService,
                    inwardService,
                    outwardFare,
                    inwardFare,
                    context,
                    isBasicAvailabilityCheck: false, // We want to check full availability when actually trying to make a reservation
                };

                const {
                    bundleAvailabilityMap,
                } = await ReservationActions.checkAvailabilityForReservation(params); // opts, isBookingFlow

                const bikeOutcome = bundleAvailabilityMap.BIKES_OUTBOUND;

                if (bikeSelected && bikeOutcome !== ReservationBundleOutcome.FULL_AVAILABILITY) {
                    setBikeAvailabilityError(true);
                    return;
                }

                let updatedTrip = trip;

                if (trip.isSeatingAvailable()) {
                    updatedTrip = updatedTrip.setSeatingPreferences(seatingPreferences);
                }

                if (bikeOutcome === ReservationBundleOutcome.FULL_AVAILABILITY && bikeSelected) {
                    updatedTrip = updatedTrip.set('bikes', bikesQty);
                }

                if (updatedTrip !== trip) {
                    TripActions.updateTrip(updatedTrip);
                }
            }

            defer(() => {
                onContinue();
                onClose();
            });
        }
    };

    const handleSeatingPreferenceChange = (field) => (event) => {
        const { value } = event.target;
        setSeatingPreferences((prev) => prev.set(field, value));
        setShowSeatPrefError(false);
    };

    const handleCheckboxChange = (field) => (event) => {
        const { checked } = event.target;
        setSeatingPreferences((prev) => prev.set(field, checked));
    };

    /*
        Don't force the user to actually set seat or bike reservation data if the context is the reservation widget.
        Just let them proceed without any selections and add the ticket to the basket.
    */
    const canProceed = context === ReservationContexts.TICKET_BOOKING ? true : (seatSelected || bikeSelected);

    const modalProps = {
        show,
        className: 'grid-modal modal--large add-reservation-modal',
        onClose,
        titleText: 'account.reservation.add-reservation-modal.title',
        headerClass: 'text-left',
        hideClose: true,
        showBack: true,
        onBack: onClose || noop,
        onContinue: handleContinue,
        disableContinue: !canProceed,
    };

    return (
        <BaseModal {...modalProps}>
            <p className="sr-only">{t('account.reservation.add-reservation-modal.title')}.</p>

            <div className="add-reservation-modal">
                <div className="title">
                    {stationDisplayName(origin)} {t('global.to')} {stationDisplayName(destination)}
                </div>

                <div className="passengers">
                    {t('booking.passengers.summary.text', { adults, children })}
                </div>

                {bikeAvailabilityError && (
                    <div className="bike-container__alert">
                        <BAlert
                            type={bAlertType.DANGER}
                            focusOnload
                        >
                            Not enough bike spaces available for your selected trip.
                        </BAlert>
                    </div>
                )}

                <div className="radio-container margin-top">
                    <div className={classNames("radio-item", { "disabled-radio": !tripHasSeatingCapability })}>
                        <Radio
                            radioClass="add-reservation-radio"
                            hasIcon={<Icon icon="seatlogo" />}
                            label={<span className="label">{t('account.reservation.add-reservation-modal.seat.title')}</span>}
                            checked={seatSelected}
                            onClick={() => {
                                if (!tripHasSeatingCapability) {
                                    return;
                                }
                                setSeatSelected(!seatSelected);
                                setShowSeatPrefError(false);
                            }}
                            isDisabled={!tripHasSeatingCapability}
                            customDisabledMessage={t('account.reservation.add-reservation-modal.seat.disabledMessage')}
                        />

                        {seatSelected && (
                            <div className="seat-container">
                                {radioGroups.map((group) => (
                                    <RadioGroups
                                        key={group.name}
                                        {...group}
                                        seatingPreferences={seatingPreferences}
                                        handleSeatingPreferenceChange={handleSeatingPreferenceChange}
                                    />
                                ))}

                                <FeatureCheckboxes
                                    seatingPreferences={seatingPreferences}
                                    handleCheckboxChange={handleCheckboxChange}
                                />

                                <div className="seat-container__message">
                                    <Icon icon="addreservationinfo" className="otrl" />
                                    <span>Seat preferences cannot be guaranteed</span>
                                </div>

                                {showSeatPrefError && (
                                    <div className="seat-container__alert">
                                        <BAlert
                                            type={bAlertType.DANGER}
                                            focusOnload
                                        >
                                            Please select a value for at least one seat preference before continuing.
                                        </BAlert>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className={classNames("radio-item", { "disabled-radio": !tripHasBikeReservationCapability })}>
                        <Radio
                            radioClass="add-reservation-radio"
                            hasIcon={<Icon icon="bike" />}
                            label={<span className="label">{t('account.reservation.add-reservation-modal.bike.title')}</span>}
                            checked={bikeSelected}
                            onClick={() => {
                                // eslint-disable-next-line
                                if (!tripHasBikeReservationCapability) return;
                                setBikeSelected(!bikeSelected);
                            }}
                            isDisabled={!tripHasBikeReservationCapability}
                            customDisabledMessage={t('account.reservation.add-reservation-modal.bike.disabledMessage')}
                        />

                        {(bikeSelected && tripHasBikeReservationCapability) && (
                            <div className="bike-container">
                                <div className="bike-container__title">
                                    {t('account.reservation.add-reservation-modal.bike.select.title')}
                                </div>
                                <div className="row">
                                    <div className="col-md-4 col-xs-4">
                                        <div className="select-container">
                                            <Icon icon="dropdown" />
                                            <label>
                                                <span className="sr-only">
                                                    {t('ariaLabel.add-reservation-modal.bikesSelect')}
                                                </span>
                                                <select
                                                    type="select"
                                                    value={bikesQty}
                                                    onChange={(e) => setBikesQty(Number(e.target.value))}
                                                >
                                                    {Array.from({ length: maxBikeQty }, (_, i) => i + 1).map((num) => (
                                                        <option key={num} value={num}>
                                                            {num}
                                                        </option>
                                                    ))}
                                                </select>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </BaseModal>
    );
};

AddReservationModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onContinue: PropTypes.func,
    onBack: PropTypes.func,
    context: PropTypes.string,
    currentJourneyData: PropTypes.object,
};

export default AddReservationModal;
